/**
 * Recovery Banner Component
 *
 * Shows a banner when autosaved configuration is available for recovery.
 */

import { RefreshCw, X, Clock } from 'lucide-react';
import { Button } from './Button';
import { formatRelativeTime } from '../../hooks/useAutosave';

interface RecoveryBannerProps {
  autosaveTime: Date;
  onRestore: () => void;
  onDismiss: () => void;
}

export function RecoveryBanner({
  autosaveTime,
  onRestore,
  onDismiss,
}: RecoveryBannerProps) {
  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-3">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-lg">
            <RefreshCw className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-200">
              Unsaved configuration found
            </p>
            <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Last saved {formatRelativeTime(autosaveTime)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onDismiss}
            className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-800"
          >
            <X className="h-4 w-4 mr-1" />
            Dismiss
          </Button>
          <Button
            size="sm"
            onClick={onRestore}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Restore
          </Button>
        </div>
      </div>
    </div>
  );
}
